import { useEffect, useState } from 'react';
import { REGIONES, comunaDisplayName } from '../config/comunas';
import type { RegionConfig } from '../config/comunas';
import type { FiberData } from '../types';

interface AddressFormProps {
  fiberData: FiberData | null;
  onSearchComuna: (comuna: string) => void;
  availableComunas?: string[];
}

interface FormData {
  region: string;
  comuna: string;
}

const FALLBACK_COMUNAS = ['CONCEPCION', 'SERENA', 'SNMIGEL'];

// Regiones que tienen al menos una comuna publicada en datos_metadata.json.
// Las comunas publicadas que no están en el catálogo caen en "Otras regiones"
// para que nunca queden inaccesibles.
const buildRegionesDisponibles = (comunas: string[]): RegionConfig[] => {
  const byDisplayName = (a: string, b: string) =>
    comunaDisplayName(a).localeCompare(comunaDisplayName(b), 'es');

  const regiones = REGIONES
    .map(region => ({
      ...region,
      comunas: region.comunas.filter(c => comunas.includes(c)).sort(byDisplayName)
    }))
    .filter(region => region.comunas.length > 0);

  const mapeadas = new Set(REGIONES.flatMap(region => region.comunas));
  const sinRegion = comunas.filter(c => !mapeadas.has(c)).sort(byDisplayName);
  if (sinRegion.length > 0) {
    regiones.push({ id: 'Otras', nombre: 'Otras regiones', comunas: sinRegion });
  }

  return regiones;
};

const AddressFormTailwind: React.FC<AddressFormProps> = ({ fiberData, onSearchComuna, availableComunas = [] }) => {
  const [formData, setFormData] = useState<FormData>({
    region: '',
    comuna: ''
  });

  const comunas = availableComunas.length > 0 ? availableComunas : FALLBACK_COMUNAS;
  const regionesDisponibles = buildRegionesDisponibles(comunas);

  // Comunas de la región seleccionada
  const comunasFiltered = formData.region
    ? regionesDisponibles.find(region => region.id === formData.region)?.comunas ?? []
    : [];

  // Format last update date
  const formatDate = (isoString: string) => {
    try {
      const date = new Date(isoString);
      return date.toLocaleDateString('es-CL', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'No disponible';
    }
  };

  const lastUpdateDate = fiberData?.metadata?.fecha_procesamiento
    ? formatDate(fiberData.metadata.fecha_procesamiento)
    : '30/10/2025';

  // Enable scroll for form page
  useEffect(() => {
    document.body.classList.add('form-page');
    return () => {
      document.body.classList.remove('form-page');
    };
  }, []);

  // Reset comuna when region changes
  useEffect(() => {
    if (formData.region) {
      setFormData(prev => ({ ...prev, comuna: '' }));
    }
  }, [formData.region]);

  // Buscar automáticamente cuando se selecciona comuna
  useEffect(() => {
    if (formData.region && formData.comuna) {
      onSearchComuna(formData.comuna);
    }
  }, [formData.region, formData.comuna, onSearchComuna]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.region || !formData.comuna) {
      alert('Por favor selecciona Región y Comuna');
      return;
    }
    onSearchComuna(formData.comuna);
  };

  return (
    <div className="flex flex-col bg-slate-100 min-h-screen">
      <main className="flex flex-1 justify-center items-center px-4 sm:px-6 py-10">
        <section className="bg-white shadow-sm border border-slate-200 rounded-2xl w-full max-w-xl">
          <div className="px-6 sm:px-10 py-8 border-slate-100 border-b text-center">
            <img src="/logo_entel.png" alt="Entel" className="mx-auto mb-5 h-20 object-contain" />
            <h1 className="font-bold text-slate-900 text-3xl">Consulta de Red Entel</h1>
            <p className="mt-2 text-slate-500 text-sm">Última actualización: {lastUpdateDate}</p>
            <p className="mt-5 text-slate-600 text-base">
              Selecciona región y comuna para visualizar la cobertura.
            </p>
          </div>

          <div className="px-6 sm:px-10 py-8">
            <form onSubmit={handleSubmit} className="space-y-6 mx-auto w-full max-w-sm">
              <div className="space-y-4">
                <div>
                  <label htmlFor="region" className="block mb-2 font-semibold text-slate-700 text-xs uppercase tracking-wider">
                    Región<span className="ml-1 text-red-500">*</span>
                  </label>
                  <select
                    id="region"
                    value={formData.region}
                    onChange={(e) => setFormData(prev => ({ ...prev, region: e.target.value }))}
                    className="bg-white px-4 py-3 border border-slate-300 focus:border-entel-blue rounded-lg outline-none focus:ring-2 focus:ring-blue-100 w-full text-base"
                    required
                  >
                    <option value="">Seleccione una región</option>
                    {regionesDisponibles.map(region => (
                      <option key={region.id} value={region.id}>{region.nombre}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="comuna" className="block mb-2 font-semibold text-slate-700 text-xs uppercase tracking-wider">
                    Comuna<span className="ml-1 text-red-500">*</span>
                  </label>
                  <select
                    id="comuna"
                    value={formData.comuna}
                    onChange={(e) => setFormData(prev => ({ ...prev, comuna: e.target.value }))}
                    className="bg-white disabled:bg-slate-100 px-4 py-3 border border-slate-300 focus:border-entel-blue rounded-lg outline-none focus:ring-2 focus:ring-blue-100 disabled:text-slate-400 w-full text-base"
                    required
                    disabled={!formData.region}
                  >
                    <option value="">Selecciona una comuna</option>
                    {comunasFiltered.map(comuna => (
                      <option key={comuna} value={comuna}>{comunaDisplayName(comuna)}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="bg-slate-50 px-4 py-3 border border-slate-200 rounded-lg text-slate-700 text-sm">
                Al seleccionar la comuna se mostrará de inmediato la cobertura general en el mapa.
              </div>

              <div className="flex justify-center pt-2">
                <button
                  type="submit"
                  className="bg-gradient-to-r from-entel-blue to-entel-blue-dark hover:opacity-95 shadow-sm px-12 py-3 rounded-lg font-bold text-white text-sm sm:text-base uppercase tracking-wide transition"
                >
                  Ver cobertura comunal
                </button>
              </div>
            </form>
          </div>
        </section>
      </main>

      <footer className="bg-[#0046B8] border-[#003A8C] border-y text-white">
        <div className="px-4 sm:px-6 lg:px-[15%] py-4">
          <div className="flex sm:flex-row flex-col sm:justify-between sm:items-center gap-3 pb-3 border-white/20 border-b">
            <div className="flex items-center gap-3">
              <img src="/logo_entel.png" alt="Entel" className="bg-white p-1.5 rounded-md h-9 object-contain" />
              <p className="font-semibold text-sm sm:text-base">Mapa de Cobertura Entel</p>
            </div>
            <a
              href="https://www.entel.cl/cobertura"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center bg-white hover:bg-slate-100 px-4 py-2 rounded-lg font-semibold text-[#0049B8] text-xs sm:text-sm whitespace-nowrap transition-colors"
            >
              Contacto y cobertura
            </a>
          </div>

          <div className="space-y-2 pt-3 text-center">
            <p className="text-blue-50 text-xs sm:text-sm leading-relaxed">
              Esta plataforma fue creada para cumplir con lo establecido en el Decreto Supremo N°176 del 6 de diciembre de 2023, publicado en el Diario Oficial el 21 de marzo de 2025, que aprueba el "Plan de Gestión y Mantención de Líneas Aéreas y Subterráneas de Telecomunicaciones".
            </p>
            <p className="text-blue-100 text-xs sm:text-sm leading-relaxed">
              Ten en cuenta que la información publicada no indica factibilidad técnica ni comercial para contratar servicios de telecomunicaciones.
            </p>
          </div>
        </div>

        <div className="bg-[#003A8C] border-white/10 border-t">
          <div className="px-4 sm:px-6 lg:px-[15%] py-2 text-blue-100 text-[11px] sm:text-xs text-center">
            Copyright Empresa Nacional de Telecomunicaciones S.A. Todos los derechos reservados.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AddressFormTailwind;
